import React from "react";
import { Box, Typography, Divider } from "@mui/material";
import Layout from "../../layouts/Layout";

const PrivacyPolicy = () => {
  return (
    <Layout>
      <Box
        sx={{
          px: { xs: 2, md: 8 },
          py: { xs: 4, md: 8 },
          maxWidth: "1200px",
          mx: "auto",
        }}
      >
        {/* Header Section */}
        <Box sx={{ textAlign: "center", mb: 6 }}>
          <Typography variant="h3" sx={{ fontWeight: 700, mb: 2 }}>
            Handpik.ai — Privacy Policy
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: "text.secondary", maxWidth: "700px", mx: "auto" }}
          >
            This Privacy Policy explains how Handpik AI, Inc. (“Handpik,” “we,”
            “us,” or “our”) collects, uses, shares, and protects information
            when you use our websites, mobile sites, apps, browser extensions,
            and related services (collectively, the “Services”). It’s designed
            to help you understand what we collect, why we collect it, and how
            you can control your information.
            <br /> <br />
            If you do not agree with this Policy, please do not use the
            Services.
          </Typography>
        </Box>

        {/* Sections */}
        <Box sx={{ mb: 5 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
            1) Information we collect
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600, mt: 1, mb: 1, ml: 3 }}
          >
            A. Information you provide to us
          </Typography>
          <Box component="ul" sx={{ pl: 6, mb: 2 }}>
            {[
              "Account details: name, email, username, password.",
              "Profile & preferences: style/fit preferences (e.g., modesty, sleeve length, hemlines, fabrics), saved items, wishlists.",
              "Communications: messages to support, survey responses, feedback, ratings/reviews.",
              "Content you upload: inspiration images/screenshots for visual search.",
              "Referral/Cashback details (if used): information required to process referral or cashback rewards and payouts.",
            ].map((item, i) => {
              const [boldPart, rest] = item.split(":");
              return (
                <Typography
                  key={i}
                  component="li"
                  sx={{ mb: 1, color: "text.secondary" }}
                >
                  •{" "}
                  <Typography component="span" sx={{ fontWeight: 600 }}>
                    {boldPart}:
                  </Typography>{" "}
                  {rest.trim()}
                </Typography>
              );
            })}
          </Box>

          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600, mt: 1, mb: 1, ml: 3 }}
          >
            B. Information collected automatically
          </Typography>
          <Box component="ul" sx={{ pl: 6, mb: 2 }}>
            {[
              "Identifiers & device data: IP address, device IDs, browser/OS type and version, language, time zone, app version.",
              "Usage & diagnostics: pages viewed, features used, timestamps, clickstream, session duration, performance logs, crash/bug reports.",
              "Cookies and similar tech: cookies, local storage, pixels, SDKs for authentication, preferences, analytics, and affiliate tracking.",
              "Approximate location: derived from IP or device settings (if enabled), used for language, sizing, or regional availability.",
            ].map((item, i) => {
              const [boldPart, rest] = item.split(":");
              return (
                <Typography
                  key={i}
                  component="li"
                  sx={{ mb: 1, color: "text.secondary" }}
                >
                  •{" "}
                  <Typography component="span" sx={{ fontWeight: 600 }}>
                    {boldPart}:
                  </Typography>{" "}
                  {rest.trim()}
                </Typography>
              );
            })}
          </Box>

          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600, mt: 1, mb: 1, ml: 3 }}
          >
            C. Information from third parties
          </Typography>
          <Box component="ul" sx={{ pl: 6, mb: 2 }}>
            {[
              "Analytics & measurement (e.g., Google Analytics or similar): aggregated usage metrics.",
              "Affiliate networks & retailers: order/commission confirmations for cashback/attribution (no payment card data from us).",
              "Anti-fraud & security: signals to protect the Services from abuse.",
            ].map((item, i) => {
              const [boldPart, rest] = item.split(":");
              return (
                <Typography
                  key={i}
                  component="li"
                  sx={{ mb: 1, color: "text.secondary" }}
                >
                  •{" "}
                  <Typography component="span" sx={{ fontWeight: 600 }}>
                    {boldPart}:
                  </Typography>{" "}
                  {rest.trim()}
                </Typography>
              );
            })}
          </Box>

          <Typography variant="body1" sx={{ mb: 1, color: "text.secondary" }}>
            We do not knowingly collect sensitive personal data unless you
            choose to share it (please don’t include sensitive info in uploads
            or chats).
          </Typography>
        </Box>

        <Divider sx={{ my: 5 }} />

        {/* Section 2 */}
        <Box sx={{ mb: 5 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
            2) How we use information
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            We use the information we collect to:
          </Typography>
          <Box component="ul" sx={{ pl: 6, mb: 2 }}>
            {[
              "Provide and secure the Services: account creation, authentication, fraud prevention, troubleshooting.",
              "Deliver features: conversational & visual search, recommendations, wishlists, price tracking, cashback/referrals.",
              "Improve Handpik: analytics, testing, research, and model training on de-identified/aggregated data.",
              "Communicate with you: service messages, updates, and—with your consent or where permitted—marketing.",
              "Comply with law: legal requests, dispute resolution, and enforcing our Terms.",
            ].map((item, i) => {
              const [boldPart, rest] = item.split(":");
              return (
                <Typography
                  key={i}
                  component="li"
                  sx={{ mb: 1, color: "text.secondary" }}
                >
                  •{" "}
                  <Typography component="span" sx={{ fontWeight: 600 }}>
                    {boldPart}:
                  </Typography>{" "}
                  {rest.trim()}
                </Typography>
              );
            })}
          </Box>
          <Typography variant="body1" sx={{ mb: 1, color: "text.secondary" }}>
            We do not sell personal information.
          </Typography>
        </Box>

        <Divider sx={{ my: 5 }} />

        {/* Section 3 */}
        <Box sx={{ mb: 5 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
            3) AI features & visual search
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, color: "text.secondary" }}>
            When you use conversational or visual search, your inputs and
            uploaded images may be processed by our models and trusted service
            providers to generate recommendations. We apply access controls,
            minimize retention of raw inputs where feasible, and use
            de-identification/aggregation to improve quality and safety.
          </Typography>
        </Box>

        <Divider sx={{ my: 5 }} />

        {/* Section 4 */}
        <Box sx={{ mb: 5 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
            4) Cookies & tracking technologies
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, color: "text.secondary" }}>
            We use cookies, pixels, SDKs, and local storage to:
          </Typography>
          <Box component="ul" sx={{ pl: 6, mb: 2 }}>
            {[
              "Keep you signed in and remember preferences.",
              "Understand how the Services are used (analytics/diagnostics).",
              "Attribute purchases to participating retailers for cashback or affiliate reporting.",
            ].map((item, i) => (
              <Typography
                key={i}
                component="li"
                sx={{ mb: 1, color: "text.secondary" }}
              >
                • {item}
              </Typography>
            ))}
          </Box>
          <Typography variant="body1" sx={{ mt: 2, color: "text.secondary" }}>
            You can adjust your browser/app settings to reject some cookies;
            certain features may not function if essential cookies are disabled.
            Where required, we obtain consent for non-essential cookies.
          </Typography>
        </Box>

        <Divider sx={{ my: 5 }} />

        {/* Section 5 */}
        <Box sx={{ mb: 5 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
            5) When we share information
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, color: "text.secondary" }}>
            We share information only as needed to operate the Services or as
            required by law:
          </Typography>
          <Box component="ul" sx={{ pl: 6, mb: 2 }}>
            {[
              "Service providers: hosting, analytics, messaging, content moderation, security, and payment partners—under confidentiality agreements.",
              "Retailers & affiliate networks: to confirm qualifying transactions for cashback/attribution.",
              "Legal & safety: to comply with legal obligations or protect rights, safety, and integrity.",
              "Business transfers: in a merger, acquisition, or sale of assets (we’ll notify you where required).",
            ].map((item, i) => {
              const [boldPart, rest] = item.split(":");
              return (
                <Typography
                  key={i}
                  component="li"
                  sx={{ mb: 1, color: "text.secondary" }}
                >
                  •{" "}
                  <Typography component="span" sx={{ fontWeight: 600 }}>
                    {boldPart}:
                  </Typography>{" "}
                  {rest.trim()}
                </Typography>
              );
            })}
          </Box>
          <Typography variant="body1" sx={{ mt: 2, color: "text.secondary" }}>
            We do not share personal information with third parties for their
            standalone marketing.
          </Typography>
        </Box>

        <Divider sx={{ my: 5 }} />

        {/* Section 6 */}
        <Box sx={{ mb: 5 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
            6) International transfers
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, color: "text.secondary" }}>
            Your information may be processed in countries other than your own.
            Where required, we use appropriate safeguards (e.g., Standard
            Contractual Clauses) and implement technical/organizational measures
            to protect your data.
          </Typography>
        </Box>

        <Divider sx={{ my: 5 }} />

        {/* Section 7 */}
        <Box sx={{ mb: 5 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
            7) Data Retention
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, color: "text.secondary" }}>
            We keep personal information only as long as necessary for the
            purposes described or as required by law. De-identified/aggregated
            data may be retained longer for analytics and service improvement.
          </Typography>
        </Box>

        <Divider sx={{ my: 5 }} />

        {/* Section 8 */}
        <Box sx={{ mb: 5 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
            8) Your choices & rights
          </Typography>
          <Box component="ul" sx={{ pl: 6, mb: 2 }}>
            {[
              "Access/Update/Delete: you can request access to, correction of, or deletion of your personal information (subject to legal limits).",
              "Marketing: opt out at any time using unsubscribe links or in settings.",
              "Cookies: manage via browser/app controls; where available, use our consent manager.",
              "Visual search uploads: remove images you’ve uploaded (subject to caching/backup windows).",
            ].map((item, i) => {
              const [boldPart, rest] = item.split(":");
              return (
                <Typography
                  key={i}
                  component="li"
                  sx={{ mb: 1, color: "text.secondary" }}
                >
                  •{" "}
                  <Typography component="span" sx={{ fontWeight: 600 }}>
                    {boldPart}:
                  </Typography>{" "}
                  {rest.trim()}
                </Typography>
              );
            })}
          </Box>
          <Typography variant="body1" sx={{ mb: 1, color: "text.secondary" }}>
            <Typography component={"span"} style={{ fontWeight: 600 }}>
              EEA/UK users (legal bases)
            </Typography>
            <br />
            We process personal data under these legal bases: contract (to
            provide the Services), legitimate interests (to improve and secure
            the Services), consent (e.g., certain cookies/marketing), and legal
            obligation. You may withdraw consent at any time. You may also have
            rights to restrict or object to processing and to data portability,
            and to complain to your local data protection authority.
            ________________________________________
          </Typography>
        </Box>

        <Divider sx={{ my: 5 }} />

        {/* Section 9 */}
        <Box sx={{ mb: 5 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
            9) Security
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, color: "text.secondary" }}>
            We use administrative, technical, and physical safeguards designed
            to protect personal information (e.g., encryption in transit, access
            controls, monitoring). No system is 100% secure; please use strong,
            unique passwords and protect your account credentials.
          </Typography>
        </Box>

        <Divider sx={{ my: 5 }} />

        {/* Section 10 */}
        <Box sx={{ mb: 5 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
            10) Children’s privacy
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, color: "text.secondary" }}>
            The Services are not directed to children under 13 (or the age
            defined by local law). We do not knowingly collect personal
            information from children. If you believe a child has provided
            information, contact us so we can delete it.
          </Typography>
        </Box>

        <Divider sx={{ my: 5 }} />

        {/* Section 11 */}
        <Box sx={{ mb: 5 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
            11) Cashback & referrals privacy specifics (if you use these
            features)
          </Typography>
          <Box component="ul" sx={{ pl: 6, mb: 2, color: "text.secondary" }}>
            {[
              "To attribute purchases, we rely on affiliate cookies/IDs set when you click through to a participating retailer.",
              "A transaction becomes “validated” only when the retailer (or its tracking partner) confirms and pays commission to Handpik.",
              "If tracking is blocked/overwritten or a transaction is returned/cancelled, rewards may not be credited.",
              "We retain the minimal data needed to investigate missing cashback/referral claims and to prevent fraud/abuse.",
            ].map((item, i) => (
              <Typography key={i} component="li" sx={{ mb: 1 }}>
                • {item}
              </Typography>
            ))}
          </Box>
        </Box>

        <Divider sx={{ my: 5 }} />

        {/* Section 12 */}
        <Box sx={{ mb: 5 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
            12) Third-party links
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, color: "text.secondary" }}>
            Our Services may link to sites or apps we don’t control. Their
            privacy practices apply to you there; please review their policies.
          </Typography>
        </Box>

        <Divider sx={{ my: 5 }} />

        {/* Section 13 */}
        <Box sx={{ mb: 5 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
            13) Changes to this Policy
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, color: "text.secondary" }}>
            We may update this Policy from time to time. Material changes will
            be posted here with a new “Last updated” date. If required by law,
            we will obtain your consent to changes.
          </Typography>
        </Box>

        <Divider sx={{ my: 5 }} />

        {/* Section 14 */}
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
          14) Contact
        </Typography>
        <Typography variant="body1" sx={{ mb: 2, color: "text.secondary" }}>
          Handpik AI, Inc.
          <br />
          Support and privacy inquiries:{" "}
          <Typography
            component="a"
            href="mailto:info@handpik.ai"
            sx={{
              color: "#FF7A00",
              textDecoration: "none",
              fontWeight: 500,
              "&:hover": {
                textDecoration: "underline",
              },
            }}
          >
            info@handpik.ai
          </Typography>
        </Typography>
      </Box>
    </Layout>
  );
};

export default PrivacyPolicy;
